import { afterNextRender, inject, Injectable, Injector } from '@angular/core';
import { environment } from '../../../environments/environment';
import { TokenData } from 'src/app/auth/models/token-data';
import { decodeTokenData } from 'src/app/auth/utils/token-utils';
import { Store } from '@ngrx/store';
import { getEmployeeById } from '@fc-store/selectors';
import { getVersion } from '@fc-shared/utils/data.utils';

type AmplitudeSdk = typeof import('@amplitude/analytics-browser');

interface AmplitudeEvent {
  name: string;
  properties: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class AmplitudeService {
  private readonly store = inject(Store);
  private readonly injector = inject(Injector);

  private sdk: AmplitudeSdk | null = null;
  private readonly eventsAwaitingSdk: AmplitudeEvent[] = [];

  initAmplitude(): void {
    if (!environment.production) {
      return;
    }
    // The SDK plus its engagement and session-replay plugins are several
    // hundred kB and nothing on screen depends on them, so they are imported
    // only once the app has painted. Events fired in the meantime are held in
    // `eventsAwaitingSdk` and flushed on load.
    afterNextRender(() => this.loadSdk(), { injector: this.injector });
  }

  track(eventName: string, properties: Record<string, unknown> = {}): void {
    if (!environment.production) {
      return;
    }

    const event: AmplitudeEvent = {
      name: eventName,
      properties: {
        user_id: this.readTokenData()?.employee_id,
        timestamp: new Date().toISOString(),
        ...properties,
      },
    };

    if (!this.sdk) {
      this.eventsAwaitingSdk.push(event);
      return;
    }
    this.sdk.track(event.name, event.properties);
  }

  private async loadSdk(): Promise<void> {
    const [analytics, engagement, sessionReplay] = await Promise.all([
      import('@amplitude/analytics-browser'),
      import('@amplitude/engagement-browser'),
      import('@amplitude/plugin-session-replay-browser'),
    ]);

    analytics.init(environment.amplitudeApiKey, {
      appVersion: getVersion(),
    });
    analytics.add(engagement.plugin());
    analytics.add(sessionReplay.sessionReplayPlugin());
    engagement.init(environment.amplitudeApiKey);

    this.sdk = analytics;
    this.identifyUser(analytics);

    for (const event of this.eventsAwaitingSdk.splice(0)) {
      analytics.track(event.name, event.properties);
    }
  }

  private identifyUser(analytics: AmplitudeSdk): void {
    const tokenData = this.readTokenData();
    if (!tokenData) {
      return;
    }
    const employee = this.store.selectSignal(
      getEmployeeById(tokenData.employee_id),
    )();

    analytics.setUserId(tokenData.employee_id);
    analytics.identify(
      new analytics.Identify()
        .set('app_user_id', tokenData.user_id)
        .set('customer_id', tokenData.customer_id)
        .set('admin', employee?.isAdmin || false),
    );
  }

  private readTokenData(): TokenData | null {
    return decodeTokenData(localStorage.getItem('token'));
  }
}
